import React, { useState, useCallback, useRef, useEffect } from 'react';
import '../styles/product-image-gallery.css';
import {
  MIN_ZOOM,
  MAX_ZOOM,
  DOUBLE_TAP_MS,
  DOUBLE_TAP_MAX_DIST,
  MIN_SWIPE_PX,
  getLightboxMaxWidth,
  THUMB_MAX_WIDTH,
  appendImageSizeParams,
  getTouchDistance,
  normalizeGalleryItems,
  useDragToScroll,
  scrollThumbIntoView,
} from './productGalleryShared';

/**
 * Галерея зображень товару для мобільної версії: свайп вліво/вправо, мініатюри внизу, lightbox з pinch-zoom і свайпом.
 * Без стрілок перемикання.
 */
const SWIPE_SNAP_MS = 250;
const INLINE_EDGE_RESISTANCE = 0.25;
const STRIP_SLIDE_PERCENT = 33.333; /* один слайд = третина стріпу */
const CLOSE_DRAG_DIRECTION_PX = 18; /* мінімум руху для вибору вертикальний/горизонтальний жест */
const CLOSE_DRAG_THRESHOLD_PX = 120; /* перетягнув більше — закрити повноекранний режим */
const CLOSE_DRAG_SNAP_MS = 280;

const ProductImageGalleryMobile = ({ items = [], showThumbnails = true }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [inlineDragX, setInlineDragX] = useState(0);
  const [inlineSlideOffset, setInlineSlideOffset] = useState(-STRIP_SLIDE_PERCENT);
  const [inlineSnapTransition, setInlineSnapTransition] = useState(false);
  const [lightboxDragX, setLightboxDragX] = useState(0);
  const [lightboxSlideOffset, setLightboxSlideOffset] = useState(-STRIP_SLIDE_PERCENT);
  const [lightboxSnapTransition, setLightboxSnapTransition] = useState(false);
  const [lightboxDragY, setLightboxDragY] = useState(0);
  const [lightboxCloseSnapTransition, setLightboxCloseSnapTransition] = useState(false);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const overlayRef = useRef(null);
  const lightboxThumbsRef = useRef(null);
  const inlineThumbsRef = useRef(null);
  const pinchStartRef = useRef(null);
  const panStartRef = useRef(null);
  const lastTapRef = useRef({ time: 0, x: 0, y: 0 });
  const inlineTouchStartRef = useRef({ x: 0, y: 0 });
  const inlineSwipeHandledRef = useRef(false);
  const lightboxSwipeStartRef = useRef({ x: 0, y: 0 });
  const lightboxCloseDragModeRef = useRef(null);
  const snapTimeoutRef = useRef(null);

  const normalizedItems = normalizeGalleryItems(items);
  const hasMultiple = normalizedItems.length > 1;
  const showStrip = showThumbnails && normalizedItems.length > 1;

  const inlineThumbsDidDragRef = useDragToScroll(inlineThumbsRef, showStrip);
  const lightboxThumbsDidDragRef = useDragToScroll(lightboxThumbsRef, lightboxOpen);

  const goTo = useCallback(
    (index) => {
      setCurrentIndex((prev) => {
        if (index < 0) return hasMultiple ? normalizedItems.length - 1 : 0;
        if (index >= normalizedItems.length) return hasMultiple ? 0 : prev;
        return index;
      });
    },
    [hasMultiple, normalizedItems.length]
  );

  const openLightbox = useCallback(() => {
    if (inlineSwipeHandledRef.current) {
      inlineSwipeHandledRef.current = false;
      return;
    }
    setLightboxOpen(true);
  }, []);

  const inlineTouchStart = useCallback(
    (e) => {
      if (!hasMultiple || !e.touches?.length) return;
      inlineTouchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      inlineSwipeHandledRef.current = false;
      setInlineSnapTransition(false);
    },
    [hasMultiple]
  );

  const inlineTouchMove = useCallback(
    (e) => {
      if (!hasMultiple || !e.touches?.length) return;
      const start = inlineTouchStartRef.current;
      let dx = e.touches[0].clientX - start.x;
      const len = normalizedItems.length;
      if (currentIndex === 0 && dx > 0) dx *= INLINE_EDGE_RESISTANCE;
      else if (currentIndex === len - 1 && dx < 0) dx *= INLINE_EDGE_RESISTANCE;
      setInlineDragX(dx);
      if (Math.abs(dx) > 5) e.preventDefault();
    },
    [hasMultiple, currentIndex, normalizedItems.length]
  );

  const inlineTouchEnd = useCallback(
    (e) => {
      if (!hasMultiple || e.changedTouches?.length !== 1) return;
      const start = inlineTouchStartRef.current;
      const endX = e.changedTouches[0].clientX;
      const dx = endX - start.x;
      if (Math.abs(dx) > 5) inlineSwipeHandledRef.current = true;
      setInlineSnapTransition(true);
      setInlineDragX(0);
      const len = normalizedItems.length;
      const goNext = Math.abs(dx) > MIN_SWIPE_PX && (dx < 0 ? currentIndex < len - 1 : currentIndex === 0 && dx > 0);
      const goPrev = Math.abs(dx) > MIN_SWIPE_PX && (dx > 0 ? currentIndex > 0 : currentIndex === len - 1 && dx < 0);
      if (goNext && dx < 0) {
        setInlineSlideOffset(-2 * STRIP_SLIDE_PERCENT);
        if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
        snapTimeoutRef.current = setTimeout(() => {
          goTo(currentIndex + 1);
          setInlineSlideOffset(-STRIP_SLIDE_PERCENT);
          setInlineSnapTransition(false);
          snapTimeoutRef.current = null;
        }, SWIPE_SNAP_MS);
      } else if (goPrev && dx > 0) {
        setInlineSlideOffset(0);
        if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
        snapTimeoutRef.current = setTimeout(() => {
          goTo(currentIndex - 1);
          setInlineSlideOffset(-STRIP_SLIDE_PERCENT);
          setInlineSnapTransition(false);
          snapTimeoutRef.current = null;
        }, SWIPE_SNAP_MS);
      } else if (goNext && dx > 0 && currentIndex === 0) {
        setInlineSlideOffset(0);
        if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
        snapTimeoutRef.current = setTimeout(() => {
          goTo(len - 1);
          setInlineSlideOffset(-STRIP_SLIDE_PERCENT);
          setInlineSnapTransition(false);
          snapTimeoutRef.current = null;
        }, SWIPE_SNAP_MS);
      } else if (goPrev && dx < 0 && currentIndex === len - 1) {
        setInlineSlideOffset(-2 * STRIP_SLIDE_PERCENT);
        if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
        snapTimeoutRef.current = setTimeout(() => {
          goTo(0);
          setInlineSlideOffset(-STRIP_SLIDE_PERCENT);
          setInlineSnapTransition(false);
          snapTimeoutRef.current = null;
        }, SWIPE_SNAP_MS);
      } else {
        if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
        snapTimeoutRef.current = setTimeout(() => {
          setInlineSnapTransition(false);
          snapTimeoutRef.current = null;
        }, SWIPE_SNAP_MS);
      }
    },
    [hasMultiple, goTo, currentIndex, normalizedItems.length]
  );

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setZoomScale(1);
    setTranslate({ x: 0, y: 0 });
    setLightboxDragY(0);
    setLightboxCloseSnapTransition(false);
  }, []);

  useEffect(() => {
    if (lightboxOpen) {
      setZoomScale(1);
      setTranslate({ x: 0, y: 0 });
      setLightboxDragX(0);
      setLightboxDragY(0);
      setLightboxSlideOffset(-STRIP_SLIDE_PERCENT);
      setLightboxSnapTransition(false);
      setLightboxCloseSnapTransition(false);
      lightboxCloseDragModeRef.current = null;
    }
  }, [currentIndex, lightboxOpen]);

  useEffect(() => {
    return () => {
      if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
    };
  }, []);

  const handleTouchStart = (e) => {
    if (!lightboxOpen) return;
    const touches = e.touches;
    if (touches.length === 2) {
      pinchStartRef.current = {
        distance: getTouchDistance(touches),
        scale: zoomScale,
      };
      panStartRef.current = null;
    } else if (touches.length === 1) {
      touchStartRef.current = { x: touches[0].clientX, y: touches[0].clientY };
      lightboxSwipeStartRef.current = { x: touches[0].clientX, y: touches[0].clientY };
      lightboxCloseDragModeRef.current = null;
      if (zoomScale > 1) {
        panStartRef.current = {
          x: touches[0].clientX,
          y: touches[0].clientY,
          translateX: translate.x,
          translateY: translate.y,
        };
      } else {
        panStartRef.current = null;
        setLightboxSnapTransition(false);
        setLightboxCloseSnapTransition(false);
      }
    }
  };

  const handleTouchMove = (e) => {
    if (!lightboxOpen) return;
    const touches = e.touches;
    if (touches.length === 2 && pinchStartRef.current) {
      e.preventDefault();
      const dist = getTouchDistance(touches);
      if (pinchStartRef.current.distance > 0) {
        const newScale = Math.min(
          MAX_ZOOM,
          Math.max(MIN_ZOOM, pinchStartRef.current.scale * (dist / pinchStartRef.current.distance))
        );
        setZoomScale(newScale);
        if (newScale <= 1) setTranslate({ x: 0, y: 0 });
      }
    } else if (touches.length === 1 && panStartRef.current && zoomScale > 1) {
      e.preventDefault();
      setTranslate({
        x: panStartRef.current.translateX + (touches[0].clientX - panStartRef.current.x),
        y: panStartRef.current.translateY + (touches[0].clientY - panStartRef.current.y),
      });
    } else if (touches.length === 1 && zoomScale === 1) {
      const start = lightboxSwipeStartRef.current;
      const dx = touches[0].clientX - start.x;
      const dy = touches[0].clientY - start.y;
      if (lightboxCloseDragModeRef.current === null) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > CLOSE_DRAG_DIRECTION_PX) {
          lightboxCloseDragModeRef.current = 'close';
        } else if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > CLOSE_DRAG_DIRECTION_PX) {
          lightboxCloseDragModeRef.current = 'horizontal';
        }
      }
      if (lightboxCloseDragModeRef.current === 'close') {
        setLightboxDragY(dy);
        e.preventDefault();
      } else if (lightboxCloseDragModeRef.current === 'horizontal' && hasMultiple) {
        let dxClamp = dx;
        const len = normalizedItems.length;
        if (currentIndex === 0 && dx > 0) dxClamp = dx * INLINE_EDGE_RESISTANCE;
        else if (currentIndex === len - 1 && dx < 0) dxClamp = dx * INLINE_EDGE_RESISTANCE;
        setLightboxDragX(dxClamp);
        if (Math.abs(dx) > 5) e.preventDefault();
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (!lightboxOpen) return;
    if (e.touches.length === 2) return;
    if (e.touches.length === 0 && e.changedTouches.length === 1) {
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const dx = endX - touchStartRef.current.x;
      const dy = endY - touchStartRef.current.y;
      const dist = Math.hypot(dx, dy);

      if (lightboxCloseDragModeRef.current === 'close') {
        if (Math.abs(dy) > CLOSE_DRAG_THRESHOLD_PX) {
          closeLightbox();
        } else {
          setLightboxCloseSnapTransition(true);
          setLightboxDragY(0);
          if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
          snapTimeoutRef.current = setTimeout(() => {
            setLightboxCloseSnapTransition(false);
            snapTimeoutRef.current = null;
          }, CLOSE_DRAG_SNAP_MS);
        }
        lightboxCloseDragModeRef.current = null;
        return;
      }

      if (dist < DOUBLE_TAP_MAX_DIST) {
        const now = Date.now();
        if (now - lastTapRef.current.time < DOUBLE_TAP_MS) {
          setZoomScale((s) => (s > 1 ? 1 : 2));
          setTranslate({ x: 0, y: 0 });
          lastTapRef.current = { time: 0, x: 0, y: 0 };
          pinchStartRef.current = null;
          panStartRef.current = null;
          return;
        }
        lastTapRef.current = { time: now, x: endX, y: endY };
      }

      if (zoomScale > 1) {
        pinchStartRef.current = null;
        panStartRef.current = null;
        return;
      }

      if (hasMultiple) {
        setLightboxSnapTransition(true);
        setLightboxDragX(0);
        const len = normalizedItems.length;
        const goNext = Math.abs(dx) > MIN_SWIPE_PX && (dx < 0 ? currentIndex < len - 1 : currentIndex === 0 && dx > 0);
        const goPrev = Math.abs(dx) > MIN_SWIPE_PX && (dx > 0 ? currentIndex > 0 : currentIndex === len - 1 && dx < 0);
        if (goNext && dx < 0) {
          setLightboxSlideOffset(-2 * STRIP_SLIDE_PERCENT);
          if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
          snapTimeoutRef.current = setTimeout(() => {
            goTo(currentIndex + 1);
            setLightboxSlideOffset(-STRIP_SLIDE_PERCENT);
            setLightboxSnapTransition(false);
            snapTimeoutRef.current = null;
          }, SWIPE_SNAP_MS);
        } else if (goPrev && dx > 0) {
          setLightboxSlideOffset(0);
          if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
          snapTimeoutRef.current = setTimeout(() => {
            goTo(currentIndex - 1);
            setLightboxSlideOffset(-STRIP_SLIDE_PERCENT);
            setLightboxSnapTransition(false);
            snapTimeoutRef.current = null;
          }, SWIPE_SNAP_MS);
        } else if (goNext && dx > 0 && currentIndex === 0) {
          setLightboxSlideOffset(0);
          if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
          snapTimeoutRef.current = setTimeout(() => {
            goTo(len - 1);
            setLightboxSlideOffset(-STRIP_SLIDE_PERCENT);
            setLightboxSnapTransition(false);
            snapTimeoutRef.current = null;
          }, SWIPE_SNAP_MS);
        } else if (goPrev && dx < 0 && currentIndex === len - 1) {
          setLightboxSlideOffset(-2 * STRIP_SLIDE_PERCENT);
          if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
          snapTimeoutRef.current = setTimeout(() => {
            goTo(0);
            setLightboxSlideOffset(-STRIP_SLIDE_PERCENT);
            setLightboxSnapTransition(false);
            snapTimeoutRef.current = null;
          }, SWIPE_SNAP_MS);
        } else {
          if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
          snapTimeoutRef.current = setTimeout(() => {
            setLightboxSnapTransition(false);
            snapTimeoutRef.current = null;
          }, SWIPE_SNAP_MS);
        }
      }
    }
    if (e.touches.length === 1) {
      panStartRef.current =
        zoomScale > 1
          ? {
              x: e.touches[0].clientX,
              y: e.touches[0].clientY,
              translateX: translate.x,
              translateY: translate.y,
            }
          : null;
    }
  };

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, closeLightbox]);

  useEffect(() => {
    if (lightboxOpen) document.body.classList.add('product-gallery-lightbox-open');
    else document.body.classList.remove('product-gallery-lightbox-open');
    return () => document.body.classList.remove('product-gallery-lightbox-open');
  }, [lightboxOpen]);

  useEffect(() => {
    if (!showStrip) return;
    scrollThumbIntoView(inlineThumbsRef.current, currentIndex, { lightbox: false });
  }, [currentIndex, showStrip]);

  useEffect(() => {
    if (!lightboxOpen || !showStrip) return;
    const t = requestAnimationFrame(() => {
      scrollThumbIntoView(lightboxThumbsRef.current, currentIndex, { lightbox: true });
    });
    return () => cancelAnimationFrame(t);
  }, [lightboxOpen, currentIndex, showStrip]);

  /* Інлайн і lightbox: центрування обраної мініатюри при зміні currentIndex. */
  if (normalizedItems.length === 0) return null;

  const current = normalizedItems[currentIndex];

  return (
    <div className="product-image-gallery product-image-gallery--mobile">
      <div className="product-gallery-inline">
        <div className="product-gallery-inline-main">
          <div
            className="product-gallery-main-wrap"
            onClick={openLightbox}
            onTouchStart={inlineTouchStart}
            onTouchMove={inlineTouchMove}
            onTouchEnd={inlineTouchEnd}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && openLightbox()}
            aria-label="Відкрити повний екран"
          >
            {hasMultiple ? (
              <div className="product-gallery-inline-track">
                <div
                  className="product-gallery-inline-strip"
                  style={{
                    transform: `translateX(calc(${inlineSlideOffset}% + ${inlineDragX}px))`,
                    transition: inlineSnapTransition ? `transform ${SWIPE_SNAP_MS}ms ease-out` : 'none',
                  }}
                >
                  {[
                    currentIndex === 0 ? normalizedItems.length - 1 : currentIndex - 1,
                    currentIndex,
                    currentIndex === normalizedItems.length - 1 ? 0 : currentIndex + 1,
                  ].map((i) => (
                    <div key={i} className="product-gallery-inline-slide">
                      <img src={normalizedItems[i].original} alt="" className="product-gallery-main-img" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <img src={current.original} alt="" className="product-gallery-main-img" />
            )}
            {hasMultiple && (
              <span className="product-gallery-expand-hint" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
              </span>
            )}
          </div>
        </div>
        {showStrip && (
          <div className="product-gallery-thumbnails product-gallery-thumbnails--draggable" ref={inlineThumbsRef}>
            {normalizedItems.map((item, i) => (
              <button
                key={i}
                type="button"
                className={`product-gallery-thumb ${i === currentIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (inlineThumbsDidDragRef.current) {
                    inlineThumbsDidDragRef.current = false;
                    return;
                  }
                  setCurrentIndex(i);
                }}
                aria-label={`Зображення ${i + 1}`}
              >
                <img src={appendImageSizeParams(item.thumbnail, { maxWidth: THUMB_MAX_WIDTH })} alt="" draggable={false} />
              </button>
            ))}
          </div>
        )}
      </div>

      {lightboxOpen && (
        <div
          ref={overlayRef}
          className="product-gallery-lightbox product-gallery-lightbox--drag-close"
          onClick={(e) => e.target === overlayRef.current && closeLightbox()}
          role="dialog"
          aria-modal="true"
          aria-label="Перегляд зображень"
        >
          <button type="button" className="product-gallery-lightbox-close" onClick={closeLightbox} aria-label="Закрити">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <div
            className="product-gallery-lightbox-drag-wrapper"
            style={{
              transform: `translateY(${lightboxDragY}px)`,
              transition: lightboxCloseSnapTransition ? `transform ${CLOSE_DRAG_SNAP_MS}ms ease-out` : 'none',
            }}
          >
            <div
              className="product-gallery-lightbox-content"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="product-gallery-lightbox-content-fade"
                style={{
                  opacity: lightboxDragY !== 0 ? Math.max(0, 1 - Math.abs(lightboxDragY) / 180) : 1,
                  transition: lightboxCloseSnapTransition ? `opacity ${CLOSE_DRAG_SNAP_MS}ms ease-out` : 'none',
                }}
                aria-hidden="true"
              />
            {hasMultiple && zoomScale === 1 ? (
              <div className="product-gallery-inline-track product-gallery-lightbox-content-inner">
                <div
                  className="product-gallery-inline-strip"
                  style={{
                    transform: `translateX(calc(${lightboxSlideOffset}% + ${lightboxDragX}px))`,
                    transition: lightboxSnapTransition ? `transform ${SWIPE_SNAP_MS}ms ease-out` : 'none',
                  }}
                >
                  {[
                    currentIndex === 0 ? normalizedItems.length - 1 : currentIndex - 1,
                    currentIndex,
                    currentIndex === normalizedItems.length - 1 ? 0 : currentIndex + 1,
                  ].map((i) => (
                    <div key={i} className="product-gallery-inline-slide">
                      <img
                        src={appendImageSizeParams(normalizedItems[i].original, { maxWidth: getLightboxMaxWidth() })}
                        alt=""
                        className="product-gallery-main-img"
                        draggable={false}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div
                className={`product-gallery-lightbox-image-wrap product-gallery-lightbox-zoom-wrap product-gallery-lightbox-content-inner ${
                  zoomScale !== 1 || translate.x !== 0 || translate.y !== 0 ? ' product-gallery-lightbox-zoomed' : ''
                }`}
                style={
                  zoomScale !== 1 || translate.x !== 0 || translate.y !== 0
                    ? { transform: `scale(${zoomScale}) translate(${translate.x}px, ${translate.y}px)` }
                    : {}
                }
              >
                <img
                  src={appendImageSizeParams(current.original, { maxWidth: getLightboxMaxWidth() })}
                  alt=""
                  className="product-gallery-lightbox-img"
                  draggable={false}
                />
              </div>
            )}
            </div>

            {showStrip && (
              <div
                className="product-gallery-lightbox-thumbnails product-gallery-thumbnails--draggable"
                ref={lightboxThumbsRef}
                style={{
                  opacity: lightboxDragY !== 0 ? Math.max(0, 1 - Math.abs(lightboxDragY) / 100) : 1,
                  transition: lightboxCloseSnapTransition ? `opacity ${CLOSE_DRAG_SNAP_MS}ms ease-out` : 'none',
                }}
              >
                <div className="product-gallery-lightbox-thumbnails-inner">
                  {normalizedItems.map((item, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`product-gallery-lightbox-thumb ${i === currentIndex ? 'active' : ''}`}
                      onClick={() => {
                        if (lightboxThumbsDidDragRef.current) {
                          lightboxThumbsDidDragRef.current = false;
                          return;
                        }
                        setCurrentIndex(i);
                      }}
                      aria-label={`Зображення ${i + 1}`}
                    >
                      <img src={appendImageSizeParams(item.thumbnail, { maxWidth: THUMB_MAX_WIDTH })} alt="" draggable={false} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductImageGalleryMobile;
